<script lang="ts">
	//Hooks
	import { Loader } from '@Shelly/shelly-design-system';
	import { createEventDispatcher, onDestroy, onMount, setContext } from 'svelte';
	//Stores
	import { os, app } from 'src/stores/session/native-bridge';
	//Services
	import { open_external_link_android } from '../../../services/native_container_requests_android';
	import { open_external_link } from '../../../services/native_container_requests_ios';
	//Config
	import { mapbox_map_config } from '../../../helpers/geolocalion_helper';

	setContext('map', { getMap: () => map });

	export let center = [23, 42],
		zoom = 2.75;
	export const fitMapToMarkers = (
		markers: {
			lat: number;
			lon: number;
		}[],
		fitPadding = 50,
		duration = 1500,
	) => {
		//@ts-ignore
		const bounds = new mapboxgl.LngLatBounds(),
			len = markers.length;
		for (let i = 0; i < len; i++) {
			const marker = markers[i];
			bounds.extend([marker.lon, marker.lat]);
		}
		try {
			map.fitBounds(bounds, {
				padding: fitPadding,
				duration,
				easing: (t) => t,
			});
		} catch (err) {
			console.warn(err, bounds);
		}
	};
	export let mapParentSize = false;
	export const goToMap = (lat: number, lng: number) => {
		map.flyTo({ center: [lng, lat], speed: 4, zoom });
	};

	let container: HTMLDivElement,
		map = null,
		mapLoaded = false,
		width = 0,
		height = 0,
		mapInfoCtrl = null,
		resizeTimeout: NodeJS.Timeout;
	let mapContainerRef: HTMLElement;
	let mapParentSizeTimeout: NodeJS.Timeout;

	const dispatcher = createEventDispatcher(),
		onLoadMapApi = () => {
			if (map == null) {
				//@ts-ignore
				mapboxgl.accessToken = mapbox_map_config.apiKey;
				//@ts-ignore
				map = new mapboxgl.Map({
					container,
					zoom,
					center,
					style: mapbox_map_config.style,
					minZoom: mapbox_map_config.minZoom,
					maxZoom: mapbox_map_config.maxZoom,
				});
				map.on('click', onClickMap);
				map.once('load', () => {
					mapLoaded = true;
					map.resize();
					dispatcher('load', map);
					preventOpenMapInfoInApp();
				});
			}
		},
		onClickMap = (e) => {
			dispatcher('click', { lat: e.lngLat.lat, lon: e.lngLat.lng });
		},
		onResizeMap = () => {
			clearTimeout(resizeTimeout);
			const updateFunc = mapParentSize ? updataMapSizeDependOnParent : updateMapHeight;
			resizeTimeout = setTimeout(updateFunc, 150);
		},
		updateMapHeight = () => {
			const page = document.querySelector('.main>.content>.page .scroll.main-layout-scroll'),
				style = getComputedStyle(page),
				bound = page.getBoundingClientRect();
			width = bound.width;
			height = bound.height;
			height -= parseFloat(style.paddingBottom);
			if (map) setTimeout(() => map.resize(), 100);
		},
		openExternalLink = (e) => {
			const href = e.target?.['href'];
			if (href) {
				e.preventDefault();
				if ($os === 'IOS') {
					open_external_link(href);
				} else {
					open_external_link_android(href);
				}
			}
		},
		preventOpenMapInfoInApp = () => {
			if ($app) {
				mapInfoCtrl = document.querySelector('.map-canvas .mapboxgl-control-container');
				if (mapInfoCtrl) mapInfoCtrl.addEventListener('click', openExternalLink);
			}
		};

	export const updataMapSizeDependOnParent = () => {
		if (mapContainerRef && map) {
			const mapParentContainer = mapContainerRef.parentElement;
			width = mapParentContainer.getBoundingClientRect().width;
			height = mapParentContainer.getBoundingClientRect().height;

			setTimeout(() => {
				map.resize();
				map.flyTo({ center: center });
			}, 100);
		} else {
			// Set map correct center and size if network is slow
			mapParentSizeTimeout = setTimeout(() => {
				updataMapSizeDependOnParent();
			}, 1500);
		}
	};

	onMount(() => {
		window.addEventListener('resize', onResizeMap);
		updateMapHeight();
	});

	onDestroy(() => {
		if (map) {
			map.off('click', onClickMap);
			map.remove();
		}
		window.removeEventListener('resize', onResizeMap);
		if (mapInfoCtrl) mapInfoCtrl.removeEventListener('click', openExternalLink);
		clearTimeout(mapParentSizeTimeout);
	});
</script>

{#if !mapLoaded}
	<div
		class="map-container d-flex justify-content-center align-items-center"
		style={`position: absolute;top: 0;width: ${width}px;height: ${height}px`}
	>
		<Loader size="l" />
	</div>
{/if}
<div bind:this={mapContainerRef} class="map-container" style="width: {width}px;height: {height}px;">
	<div class="map-canvas" bind:this={container} />
	{#if map}
		<slot />
	{/if}
</div>

<svelte:head>
	<link rel="stylesheet" href="https://api.mapbox.com/mapbox-gl-js/v2.9.1/mapbox-gl.css" />
	<script
		src="https://api.mapbox.com/mapbox-gl-js/v2.9.1/mapbox-gl.js"
		on:load={onLoadMapApi}
	></script>
</svelte:head>

<style>
	.map-container {
		width: 100%;
		height: 100%;
		max-width: 100%;
		max-height: 100%;
		overflow: hidden;
		position: relative;
	}
	.map-canvas {
		width: 100%;
		height: 100%;
		border: 1px solid var(--color-3);
		border-radius: var(--radius-big);
	}
	:global(.map-canvas .mapboxgl-control-container) {
		opacity: 0.5;
	}
</style>
